import {
    AutoAdjustLabelSettings,
    LabelerModuleSettings,
    MultiPrintSettings,
    NetworkPrinterSettings,
    PrintModuleId,
    PrinterProfile,
} from '../../../store/reducers/userDataReducer';

export const DEFAULT_PRINTER_PORT = '9100';
export const DEFAULT_PRINT_WIDTH = '624';

export const PRINT_MODULE_LABELER: PrintModuleId = 'labeler';
export const PRINT_MODULE_TRACKER: PrintModuleId = 'tracker';
export const PRINT_MODULE_PRICE_UPDATE: PrintModuleId = 'priceUpdate';
export const PRINT_MODULE_PRODUCT_DETAIL: PrintModuleId = 'productDetail';

export const PRINT_MODULE_LABELS: Record<PrintModuleId, string> = {
    labeler: 'Labeler',
    tracker: 'Expiring Stock Tracker',
    priceUpdate: 'Price Update',
    productDetail: 'Product Detail',
};

export function createPrinterProfileId(): string {
    return `profile_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function createEmptyPrinterProfile(name = ''): PrinterProfile {
    return {
        id: createPrinterProfileId(),
        name,
        networkPrinterSettings: {
            ip: '',
            port: DEFAULT_PRINTER_PORT,
        },
        autoAdjustLabel: {
            isAutoAdjustEnabled: false,
            currentPrintWidth: DEFAULT_PRINT_WIDTH,
        },
        multiPrintSettings: {
            isMultiPrintEnabled: false,
            isResetPrintQuantity: true,
        },
        reportEmail: null,
    };
}

export function getPrinterProfileById(
    labelerModule: LabelerModuleSettings,
    profileId: string | undefined | null,
): PrinterProfile | null {
    if (!profileId) {
        return null;
    }
    return labelerModule.printerProfiles.find((profile) => profile.id === profileId) || null;
}

/** Resolve the profile selected for a print module, or null if unset / deleted. */
export function getPrinterProfileForModule(
    labelerModule: LabelerModuleSettings,
    moduleId: PrintModuleId,
): PrinterProfile | null {
    const profileId = labelerModule.modulePrinterProfileIds[moduleId];
    return getPrinterProfileById(labelerModule, profileId);
}

/** Resolve the label template id selected for a print module (independent of its printer profile). */
export function getModuleTemplateId(
    labelerModule: LabelerModuleSettings,
    moduleId: PrintModuleId,
): number | null {
    return labelerModule.moduleTemplateIds[moduleId] ?? null;
}

export function getModuleNetworkSettings(
    labelerModule: LabelerModuleSettings,
    moduleId: PrintModuleId,
): NetworkPrinterSettings | null {
    const profile = getPrinterProfileForModule(labelerModule, moduleId);
    return profile ? profile.networkPrinterSettings : null;
}

export function getModuleAutoAdjustSettings(
    labelerModule: LabelerModuleSettings,
    moduleId: PrintModuleId,
): AutoAdjustLabelSettings | null {
    const profile = getPrinterProfileForModule(labelerModule, moduleId);
    return profile ? profile.autoAdjustLabel : null;
}

export function getModuleMultiPrintSettings(
    labelerModule: LabelerModuleSettings,
    moduleId: PrintModuleId,
): MultiPrintSettings | null {
    const profile = getPrinterProfileForModule(labelerModule, moduleId);
    return profile ? profile.multiPrintSettings : null;
}

/** Clear module selections that pointed at a deleted profile. */
export function clearModuleSelectionsForProfile(
    modulePrinterProfileIds: Partial<Record<PrintModuleId, string>>,
    deletedProfileId: string,
): Partial<Record<PrintModuleId, string>> {
    const next: Partial<Record<PrintModuleId, string>> = { ...modulePrinterProfileIds };
    (Object.keys(next) as PrintModuleId[]).forEach((moduleId) => {
        if (next[moduleId] === deletedProfileId) {
            delete next[moduleId];
        }
    });
    return next;
}

export function upsertPrinterProfileInList(
    profiles: PrinterProfile[],
    profile: PrinterProfile,
): PrinterProfile[] {
    const index = profiles.findIndex((item) => item.id === profile.id);
    if (index === -1) {
        return [...profiles, profile];
    }
    const next = [...profiles];
    next[index] = profile;
    return next;
}

export function removePrinterProfileFromList(
    profiles: PrinterProfile[],
    profileId: string,
): PrinterProfile[] {
    return profiles.filter((profile) => profile.id !== profileId);
}
